import type { ReactNode, Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';

export interface EffectiveCardCascade {
  /** Prefix label, e.g. "Source". */
  label: string;
  /** Cascade levels in resolution order, e.g. Line/User override → rule. */
  items: string[];
  /** The item that won (highlighted). */
  active: string;
}

export interface EffectiveCardProps {
  /** Leading icon (MUI). */
  icon: SvgIconComponent;
  /** Small uppercase eyebrow, e.g. "Effective budget". */
  eyebrow: string;
  /** "Resolved" lock affordance copy. */
  resolvedLabel: string;
  /** The resolved entity name (large). */
  name: string;
  /** One-line detail, e.g. "10 GB data · $60.00/mo · up to 1 line". */
  detail?: ReactNode;
  /** Optional cascade indicator (budget). */
  cascade?: EffectiveCardCascade;
  /** Optional one-line resolution note (package). */
  note?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * The read-only "resolved entitlement" hero card (prototype `EffectiveCard`):
 * an info-tinted card with a left rail, a leading icon, an eyebrow + a
 * "Resolved" lock affordance, the resolved name + one-line detail, and either a
 * cascade-source indicator (budget) or a resolution note (package). Info-toned
 * so it reads as derived/authoritative, not editable.
 */
export function EffectiveCard({
  icon: Icon,
  eyebrow,
  resolvedLabel,
  name,
  detail,
  cascade,
  note,
  ref,
}: EffectiveCardProps) {
  return (
    <Card
      ref={ref}
      variant="outlined"
      sx={(theme) => ({
        borderRadius: '6px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
        borderLeft: `4px solid ${theme.palette.info.main}`,
        p: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={(theme) => ({
            width: 34,
            height: 34,
            borderRadius: '8px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(theme.palette.info.main, 0.12),
          })}
        >
          <Icon sx={{ fontSize: 18, color: 'info.dark' }} />
        </Box>
        <Typography
          component="span"
          sx={{
            fontFamily: (theme) => theme.typography.button.fontFamily,
            fontWeight: 600,
            fontSize: 11,
            lineHeight: 1.2,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: 'info.dark',
          }}
        >
          {eyebrow}
        </Typography>
        <Box
          sx={{
            ml: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.625,
            color: 'text.secondary',
          }}
        >
          <LockOutlined sx={{ fontSize: 11 }} />
          <Typography
            component="span"
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 500,
              fontSize: 10,
              lineHeight: 1,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {resolvedLabel}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: (theme) => theme.typography.button.fontFamily,
          fontWeight: 700,
          fontSize: 20,
          lineHeight: 1.15,
          letterSpacing: '-0.01em',
          color: 'text.primary',
        }}
      >
        {name}
      </Typography>

      {detail && (
        <Typography
          sx={{ fontWeight: 500, fontSize: 13.5, lineHeight: 1.4, color: 'text.primary' }}
        >
          {detail}
        </Typography>
      )}

      {cascade && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: '2px' }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              fontSize: 11,
              lineHeight: 1,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mr: '2px',
            }}
          >
            {cascade.label}
          </Typography>
          {cascade.items.map((item, i) => {
            const on = item === cascade.active;
            return (
              <Box
                key={item}
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
              >
                {i > 0 && <ChevronRightOutlined sx={{ fontSize: 12, color: 'text.secondary' }} />}
                <Box
                  component="span"
                  sx={(theme) => ({
                    fontFamily: theme.typography.button.fontFamily,
                    fontWeight: on ? 700 : 500,
                    fontSize: 11,
                    lineHeight: 1,
                    px: 1,
                    py: '3px',
                    borderRadius: 999,
                    bgcolor: on ? alpha(theme.palette.info.main, 0.14) : 'transparent',
                    color: on ? 'info.dark' : 'text.secondary',
                    border: `1px solid ${on ? alpha(theme.palette.info.main, 0.4) : 'transparent'}`,
                  })}
                >
                  {item}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {note && (
        <Typography
          sx={{ fontWeight: 400, fontSize: 12, lineHeight: 1.4, color: 'text.secondary' }}
        >
          {note}
        </Typography>
      )}
    </Card>
  );
}
